import { useGetHeliosDataTablesQuery } from 'store/services/helios/dataHelios';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormSetValue } from 'react-hook-form';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { Table } from 'components/common/Table/Table';
import { ActionModal } from 'components/mui/common/ActionModal';
import type { HeliosBrowseFilterControls } from 'components/user/DummyHelios/Listing/components/HeliosBrowseFilterControls';
import { useTableQueryControls } from 'utils/hooks';
import { useHeliosBrowseFilters } from 'utils/hooks/userAgendaTable/useHeliosBrowseFilters';
import type { HeliosRecordDataTablesColumn, HeliosRecordDataTablesReq } from 'types/dataHelios';
import { generateTableSchemaAndData, type HeliosSchemeType } from 'utils/table/user/helios/listing';

import type { ModalsType } from 'types';
import type { FormData } from './FunctionModal';
import { SearchField } from 'components/common/Table/actions';
import { computeDisplayColumnsTextFromRow } from 'utils/helios/displayColumns';
import { isMobileApp } from 'utils/common';

interface Props extends ModalsType {
  setValue?: UseFormSetValue<FormData>;
  value?: string | string[];
  templateId: string;
  fieldName?: string;
  onSubmit?: (selectedItem: { id: string; name: string }) => void;
  onMultiSubmit?: (selectedItems: { id: string; name: string }[]) => void;
  title?: string;
  initialSearchTerm?: string;
  isAutoSelectSearchResultEnabled?: boolean;
  isMultiSelect?: boolean;
  renderFilterControls?: (props: ComponentProps<typeof HeliosBrowseFilterControls>) => ReactNode;
  functionCallDto?: HeliosRecordDataTablesReq['functionCallDto'];
  sx?: SxProps<Theme>;
}

const getBrowseNameFromRow = (
  rowData: Record<string, unknown>,
  columns: Array<{ itemId: number; columnKey: string; label: string; order: number }>
): string => {
  const sortedColumns = [...columns].sort((a, b) => a.order - b.order);
  const firstTwoColumns = sortedColumns.slice(0, 2);

  const displayColumns = firstTwoColumns.map((col, index) => ({
    itemId: col.itemId,
    order: index + 1,
  }));

  return computeDisplayColumnsTextFromRow(rowData, columns, displayColumns) || '';
};

const tableInnerContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

const tableContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '& > div': tableInnerContainerSx,
  '& > div > div': tableInnerContainerSx,
} as const;

export const SelectBrowseModal = memo(
  ({
    open,
    onClose,
    setValue,
    value,
    templateId,
    fieldName,
    onSubmit,
    onMultiSubmit,
    title,
    initialSearchTerm,
    isAutoSelectSearchResultEnabled,
    isMultiSelect,
    renderFilterControls,
    functionCallDto,
    sx,
  }: Props) => {
    const { t } = useTranslation();
    const mobileApp = isMobileApp();
    const selectedRows = useRef<string[]>([]);
    const [currentSelection, setCurrentSelection] = useState<string[]>([]);
    const valueRef = useRef(value);
    valueRef.current = value;
    const [isAutoSelectEnabled, setIsAutoSelectEnabled] = useState(false);

    const { state, setPagination, setSearch } = useTableQueryControls<HeliosSchemeType, Record<string, string>>(
      `HeliosSelectBrowse-${templateId}`,
      {
        filters: {},
        search: '',
        pagination: { page: 1, pageSize: 10 },
      },
      { syncPaginationWithUrl: false }
    );

    const columnsRef = useRef<HeliosRecordDataTablesColumn[] | undefined>(undefined);
    const browsePageSize = state?.pagination?.pageSize ?? 10;
    const showFilters = Boolean(renderFilterControls);
    const browseFilters = useHeliosBrowseFilters({
      templateId: open && showFilters ? templateId : '',
      open: Boolean(open && showFilters),
      columnsRef,
      setPagination,
      pageSize: browsePageSize,
    });

    const prioritizeRecordIds = useMemo(() => {
      if (!open || !isMultiSelect) {
        return undefined;
      }

      if (Array.isArray(value) && value.length > 0) {
        const ids = value.map((id: string) => Number(id)).filter((id: number) => !isNaN(id));
        return ids.length > 0 ? ids : undefined;
      }

      return undefined;
    }, [isMultiSelect, open, value]);

    const { data, isFetching } = useGetHeliosDataTablesQuery(
      {
        templateId: templateId ?? '',
        filters: browseFilters.apiFilters,
        searchKeyword: state?.search ?? '',
        offset: ((state?.pagination?.page ?? 1) - 1) * (state?.pagination?.pageSize ?? 10),
        limit: state?.pagination?.pageSize ?? 10,
        orderBy: state?.sort?.by ?? '',
        orderDesc: state?.sort?.order === 'desc',
        useServiceAccount: false,
        prioritizeRecordIds,
        functionCallDto,
      },
      {
        skip: !templateId || !open,
      }
    );

    columnsRef.current = data?.columns ?? columnsRef.current;

    useEffect(() => {
      if (!open) return;

      const currentValue = valueRef.current;

      if (Array.isArray(currentValue)) {
        selectedRows.current = currentValue;
        setCurrentSelection(currentValue);
        return;
      }

      if (currentValue) {
        selectedRows.current = [currentValue];
        setCurrentSelection([currentValue]);
        return;
      }

      selectedRows.current = [];
      setCurrentSelection([]);
    }, [open]);

    useEffect(() => {
      if (!open) {
        return;
      }

      if (initialSearchTerm) {
        setSearch(initialSearchTerm);
      }

      setIsAutoSelectEnabled(Boolean(isAutoSelectSearchResultEnabled));
    }, [initialSearchTerm, isAutoSelectSearchResultEnabled, open, setSearch]);

    const { transformedData, tableScheme } = useMemo(() => {
      return generateTableSchemaAndData(data?.columns, data?.data);
    }, [data]);

    const handleClose = useCallback(() => {
      selectedRows.current = [];
      setCurrentSelection([]);

      setSearch('');
      setPagination({
        page: 1,
        pageSize: state?.pagination?.pageSize ?? 10,
      });

      setIsAutoSelectEnabled(false);
      onClose();
    }, [onClose, setPagination, setSearch, state?.pagination?.pageSize]);

    const handleSubmit = useCallback(
      (selectedRecordId = selectedRows.current[0]) => {
        if (isMultiSelect) {
          const selectedIds = selectedRows.current;
          const items = selectedIds
            .map((id) => {
              const rowIndex = transformedData.findIndex((row: Record<string, unknown>) => String(row.id) === String(id));
              return {
                id,
                name: rowIndex !== -1 && data?.columns ? getBrowseNameFromRow(transformedData[rowIndex], data.columns) : '',
              };
            })
            .filter((item) => Boolean(item.id));

          if (onMultiSubmit) {
            onMultiSubmit(items);
          } else if (setValue && fieldName) {
            setValue(fieldName, items);
          }

          handleClose();
          return;
        }

        if (!transformedData || transformedData.length === 0) {
          handleClose();
          return;
        }

        const selectedRowIndex = transformedData.findIndex((row: Record<string, unknown>) => String(row.id) === String(selectedRecordId));

        const selectedItem = {
          id: selectedRecordId,
          name: selectedRowIndex !== -1 && data?.columns ? getBrowseNameFromRow(transformedData[selectedRowIndex], data.columns) : '',
        };

        if (onSubmit) {
          onSubmit(selectedItem);
        } else if (setValue && fieldName) {
          setValue(fieldName, selectedItem);
        }

        handleClose();
      },
      [data, handleClose, setValue, fieldName, onSubmit, onMultiSubmit, isMultiSelect, transformedData]
    );

    const updateSelection = useCallback((rows: string[]) => {
      setCurrentSelection(rows);
    }, []);

    const handleSelectRow = useCallback(
      (rowIds: string[]) => {
        selectedRows.current = rowIds;
        updateSelection(rowIds);
      },
      [updateSelection]
    );

    useEffect(() => {
      if (!open || !isAutoSelectEnabled || isFetching || isMultiSelect) {
        return;
      }

      if (transformedData.length === 1) {
        const selectedRecordId = String(transformedData[0].id ?? '');

        if (selectedRecordId) {
          selectedRows.current = [selectedRecordId];
          updateSelection([selectedRecordId]);
          setIsAutoSelectEnabled(false);
          handleSubmit(selectedRecordId);
          return;
        }
      }

      setIsAutoSelectEnabled(false);
    }, [handleSubmit, isAutoSelectEnabled, isFetching, isMultiSelect, open, transformedData, updateSelection]);

    return (
      <ActionModal
        maxWidth="md"
        open={open}
        onClose={handleClose}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        title={title ?? t('common.select')}
        sx={sx}>
        <Box sx={mobileApp ? tableContainerSx : undefined}>
          <Table
            rows={data?.count ?? 0}
            page={state?.pagination?.page ?? 1}
            pageSize={state?.pagination?.pageSize ?? 10}
            isLoading={isFetching}
            data={transformedData}
            dataSchema={tableScheme}
            onPageSizeChange={(pageSize) => {
              setPagination({
                page: 1,
                pageSize,
              });
            }}
            onCurrentPageChange={(page) => {
              setPagination({
                page,
                pageSize: state?.pagination?.pageSize ?? 10,
              });
            }}
            onSelectRow={handleSelectRow}
            defaultSelectedRows={currentSelection}
            isMultiSelect={!!isMultiSelect}
            selectOnRowClick
            controls={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <SearchField
                  placeholder={t('common.search._')}
                  value={state?.search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={isFetching}
                  clearable
                />
                {renderFilterControls?.({ modalOpen: open, columns: data?.columns ?? [], browseFilters })}
              </Box>
            }
          />
        </Box>
      </ActionModal>
    );
  }
);
